import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { lspAPI, adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  GitMerge, Clock, CheckCircle, XCircle, DollarSign, 
  Calendar, MapPin, ArrowRight, RefreshCw, Building2,
  AlertTriangle, Shield
} from 'lucide-react';

const LSPCommitments = () => {
  const [commitments, setCommitments] = useState([]);
  const [deals, setDeals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const commitmentsRes = await lspAPI.getCommitments();
      setCommitments(commitmentsRes.data);
      
      // We don't have direct access to deal details as LSP
      // The deal info would need to be included in commitment response or fetched separately
      // For now, we'll show what we have from commitments
    } catch (error) {
      toast.error('Failed to load commitments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      requested: { 
        color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', 
        icon: Clock,
        label: 'Pending Review'
      },
      admin_approved: { 
        color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', 
        icon: CheckCircle,
        label: 'Approved'
      },
      admin_declined: { 
        color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 
        icon: XCircle,
        label: 'Declined'
      },
      lsp_confirmed: { 
        color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', 
        icon: Shield,
        label: 'Confirmed'
      },
      funds_verified: { 
        color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', 
        icon: DollarSign,
        label: 'Funds Verified'
      },
    };
    
    const config = statusConfig[status] || statusConfig.requested;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout title="My Commitments">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="My Commitments">
      <div className="space-y-6 animate-slide-in-up" data-testid="lsp-commitments">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Commitments</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{commitments.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Pending</p>
              <p className="text-2xl font-bold text-blue-600">
                {commitments.filter(c => c.commitment_status === 'requested').length}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {commitments.filter(c => c.commitment_status === 'admin_approved').length}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Funds Verified</p>
              <p className="text-2xl font-bold text-emerald-600">
                {commitments.filter(c => c.commitment_status === 'funds_verified').length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Commitments List */}
        {commitments.length === 0 ? (
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <GitMerge className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                No Commitments Yet
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                You haven't committed to any deals yet. Browse the marketplace to find deals.
              </p>
              <Link to="/lsp/marketplace">
                <Button className="gradient-lsp border-0">
                  <Building2 className="w-4 h-4 mr-2" />
                  Browse Marketplace
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {commitments.map((commitment) => (
              <Card 
                key={commitment.id}
                className="glass-effect border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all"
                data-testid={`commitment-card-${commitment.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 gradient-lsp rounded-xl flex items-center justify-center flex-shrink-0">
                        <GitMerge className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            Deal Commitment
                          </h3>
                          {getStatusBadge(commitment.commitment_status)}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Deal ID: {commitment.deal_id.slice(0, 12)}...
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Committed: {new Date(commitment.created_at).toLocaleDateString()}
                        </p>
                        {commitment.commitment_amount && (
                          <p className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">
                            Amount: {formatCurrency(commitment.commitment_amount)}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      {commitment.commitment_status === 'requested' && (
                        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                          <Clock className="w-4 h-4" />
                          <span>Awaiting admin review</span>
                        </div>
                      )}
                      {commitment.commitment_status === 'admin_approved' && (
                        <div className="text-sm text-green-600 dark:text-green-400">
                          <p className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Approved! Prepare funds for verification.
                          </p>
                        </div>
                      )}
                      {commitment.commitment_status === 'admin_declined' && (
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                          <XCircle className="w-4 h-4" />
                          <span>Commitment declined</span>
                        </div>
                      )}
                      {commitment.commitment_status === 'funds_verified' && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                          <Shield className="w-4 h-4" />
                          <span>Ready for closing</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status Timeline */}
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between text-xs">
                      <div className={`flex flex-col items-center ${['requested', 'admin_approved', 'lsp_confirmed', 'funds_verified'].includes(commitment.commitment_status) ? 'text-purple-600 dark:text-purple-400' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${['requested', 'admin_approved', 'lsp_confirmed', 'funds_verified'].includes(commitment.commitment_status) ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          <Clock className="w-4 h-4" />
                        </div>
                        <span>Submitted</span>
                      </div>
                      <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-700 mx-2" />
                      <div className={`flex flex-col items-center ${['admin_approved', 'lsp_confirmed', 'funds_verified'].includes(commitment.commitment_status) ? 'text-green-600 dark:text-green-400' : commitment.commitment_status === 'admin_declined' ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${['admin_approved', 'lsp_confirmed', 'funds_verified'].includes(commitment.commitment_status) ? 'bg-green-100 dark:bg-green-900/30' : commitment.commitment_status === 'admin_declined' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          {commitment.commitment_status === 'admin_declined' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </div>
                        <span>Review</span>
                      </div>
                      <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-700 mx-2" />
                      <div className={`flex flex-col items-center ${['lsp_confirmed', 'funds_verified'].includes(commitment.commitment_status) ? 'text-purple-600 dark:text-purple-400' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${['lsp_confirmed', 'funds_verified'].includes(commitment.commitment_status) ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          <Shield className="w-4 h-4" />
                        </div>
                        <span>Confirmed</span>
                      </div>
                      <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-700 mx-2" />
                      <div className={`flex flex-col items-center ${commitment.commitment_status === 'funds_verified' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${commitment.commitment_status === 'funds_verified' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          <DollarSign className="w-4 h-4" />
                        </div>
                        <span>Verified</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Browse More CTA */}
        {commitments.length > 0 && (
          <Card className="glass-effect border-purple-200 dark:border-purple-800 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">Looking for more deals?</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Browse available deals in the marketplace</p>
                </div>
                <Link to="/lsp/marketplace">
                  <Button className="gradient-lsp border-0">
                    <Building2 className="w-4 h-4 mr-2" />
                    Browse Marketplace
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default LSPCommitments;
